import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, FolderOpen, TrendingUp } from "lucide-react";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { useMultiPeriodCurrencyController } from "../hooks/useMultiPeriodCurrencyController";
import { MultiPeriodSelector } from "./MultiPeriodSelector";
import { CurrencyTrendChart } from "./CurrencyTrendChart";
import { CurrencyLineChart } from "./CurrencyLineChart";

export const MultiPeriodCurrencyView: React.FC = () => {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const controller = useMultiPeriodCurrencyController();

  if (!selectedFund) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center min-h-[400px]">
        <div className="bg-primary/10 p-6 rounded-full mb-6">
          <FolderOpen className="h-12 w-12 text-primary" />
        </div>
        <h2 className="text-2xl font-semibold mb-2">No Fund Selected</h2>
        <p className="text-muted-foreground mb-6 max-w-md">
          Please select a fund from the sidebar to view currency trends across multiple reporting periods.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-8xl space-y-6">
     
      {/* Period Selection */}
      <MultiPeriodSelector
        availablePeriods={controller.availablePeriods}
        selectedPeriods={controller.selectedPeriods}
        onAddPeriod={controller.addPeriod}
        onRemovePeriod={controller.removePeriod}
        onClearAll={controller.clearAllPeriods}
        onSelectLastN={controller.selectLastNPeriods}
        onSelectAll={controller.selectAllPeriods}
        isPeriodSelected={controller.isPeriodSelected}
        isLoading={controller.isLoading}
      />

      {/* Info Alert */}
      {controller.selectedPeriods.length === 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Get Started</AlertTitle>
          <AlertDescription>
            Select multiple reporting periods above to analyze how currency values change over time. 
            This analysis will help you understand portfolio currency exposure trends and identify patterns.
          </AlertDescription>
        </Alert>
      )}

      {/* Currency Charts */}
      {controller.selectedPeriods.length > 0 && (
        <div className="space-y-6">
          {/* Line Chart */}
          <CurrencyLineChart
            periodHoldingsData={controller.periodHoldingsData}
            isLoading={controller.isLoading}
          />
          
          {/* Data Table */}
          <CurrencyTrendChart
            periodHoldingsData={controller.periodHoldingsData}
            isLoading={controller.isLoading}
          />
        </div>
      )}

      {/* Analysis Summary */}
      {controller.periodHoldingsData.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Analysis Summary</CardTitle>
            <CardDescription>Overview of selected periods</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Periods Analyzed:</span>
                <span className="font-semibold">{controller.selectedPeriods.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date Range:</span>
                <span className="font-semibold text-right">
                  {new Date(controller.selectedPeriods[0]?.period).toLocaleDateString()} - {' '}
                  {new Date(controller.selectedPeriods[controller.selectedPeriods.length - 1]?.period).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Holdings:</span>
                <span className="font-semibold">
                  {controller.periodHoldingsData.reduce((sum, data) => sum + data.holdings.length, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unique Currencies:</span>
                <span className="font-semibold">
                  {new Set(
                    controller.periodHoldingsData.flatMap(data => 
                      data.holdings.map(h => h.currency || 'USD')
                    )
                  ).size}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 